import type { WheelParty } from "@/entities/pvp-roulette/model/commands"
import type { RoundSnapshot } from "@/shared/api/types/pvpRoulette"
import { getActiveBets } from "@/shared/api/types/pvpRoulette"
import {
  SOLO_CURRENT_USER_COLOR,
  TEAM_1_COLOR,
} from "@/shared/constants/pvpRoulette"
import { findSoloPartyForUser } from "./pvpWheelUserIdentity"

/** Figma Background/Tetriary */
export const TRICKS_OTHERS_COLOR_A = 0x4a3762
/** Figma Background/Secondary */
export const TRICKS_OTHERS_COLOR_B = 0x342744

export function lerpSectorColor(from: number, to: number, t: number): number {
  const clamp = Math.min(1, Math.max(0, t))
  const fr = (from >> 16) & 0xff
  const fg = (from >> 8) & 0xff
  const fb = from & 0xff
  const tr = (to >> 16) & 0xff
  const tg = (to >> 8) & 0xff
  const tb = to & 0xff
  const r = Math.round(fr + (tr - fr) * clamp)
  const g = Math.round(fg + (tg - fg) * clamp)
  const b = Math.round(fb + (tb - fb) * clamp)
  return (r << 16) | (g << 8) | b
}

export function userParticipatesInWheelRound(
  snapshot: RoundSnapshot,
  userId: string | undefined
): boolean {
  if (!userId) return false
  if (getActiveBets(snapshot.bets).some((b) => b.userData.userId === userId)) {
    return true
  }
  return findSoloPartyForUser(snapshot, userId) != null
}

export function userParticipatesInWheelParties(
  parties: WheelParty[],
  userId: string | undefined
): boolean {
  if (!userId || parties.length === 0) return false
  return parties.some((p) => isUserWheelSector(p, userId))
}

export function isUserWheelSector(p: WheelParty, userId: string): boolean {
  if (p.partyType === "TEAM") {
    return p.members?.some((m) => m.userId === userId) ?? false
  }
  if (p.userId === userId) return true
  return p.members?.some((m) => m.userId === userId) ?? false
}

function pickOtherTricksColor(avoid: number): number {
  return avoid === TRICKS_OTHERS_COLOR_A
    ? TRICKS_OTHERS_COLOR_B
    : TRICKS_OTHERS_COLOR_A
}

/**
 * TRICKS palette for non-user sectors: alternate #4A3762 / #342744 around the wheel
 * so neighbours never share the same fill. User sector keeps its color (e.g. green).
 */
export function applyTricksPhaseSectorColors(
  parties: WheelParty[],
  userId: string | undefined
): WheelParty[] {
  if (!userId || parties.length === 0) return parties

  const n = parties.length
  const assigned: number[] = new Array(n)
  let lastOtherColor: number | null = null

  const prevOtherAssignedColor = (index: number): number | null => {
    for (let step = 1; step < n; step++) {
      const j = (index - step + n) % n
      if (!isUserWheelSector(parties[j]!, userId)) {
        const c = assigned[j]
        return c != null ? c : null
      }
    }
    return null
  }

  for (let i = 0; i < n; i++) {
    const p = parties[i]!
    if (isUserWheelSector(p, userId)) {
      assigned[i] =
        p.partyType === "TEAM" ? (p.color ?? TEAM_1_COLOR) : SOLO_CURRENT_USER_COLOR
      continue
    }
    const prevColor = prevOtherAssignedColor(i)
    if (prevColor === TRICKS_OTHERS_COLOR_A) {
      assigned[i] = TRICKS_OTHERS_COLOR_B
    } else if (prevColor === TRICKS_OTHERS_COLOR_B) {
      assigned[i] = TRICKS_OTHERS_COLOR_A
    } else {
      assigned[i] =
        lastOtherColor === TRICKS_OTHERS_COLOR_A
          ? TRICKS_OTHERS_COLOR_B
          : TRICKS_OTHERS_COLOR_A
    }
    lastOtherColor = assigned[i]!
  }

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    const pi = parties[i]!
    const pj = parties[j]!
    if (isUserWheelSector(pi, userId) || isUserWheelSector(pj, userId)) continue
    if (assigned[i] === assigned[j]) {
      assigned[j] = pickOtherTricksColor(assigned[i]!)
    }
  }

  return parties.map((p, i) => ({ ...p, color: assigned[i] }))
}

export function wheelPartiesColorsEqual(
  a: WheelParty[],
  b: WheelParty[]
): boolean {
  if (a.length !== b.length) return false
  return a.every((p, i) => (p.color ?? 0) === (b[i]?.color ?? 0))
}

export function partiesUseTricksPalette(parties: WheelParty[]): boolean {
  return parties.some(
    (p) =>
      p.color === TRICKS_OTHERS_COLOR_A || p.color === TRICKS_OTHERS_COLOR_B
  )
}

/** Muted non-user sectors from SPINNING through SLOWING (was TRICKS-only). */
export function shouldApplyMutedWheelSectorColors(status: string): boolean {
  return status === "SPINNING" || status === "TRICKS" || status === "SLOWING"
}
